import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useLanguage } from '../contexts/LanguageContext'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: string
  structuredData?: object
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image = "https://logicloud.mx/logo.png",
  url = "https://logicloud.mx/",
  type = "website",
  structuredData
}) => {
  const { language } = useLanguage()

  // Dynamic SEO content based on language
  const seoContent = {
    en: {
      title: title || "LogiCloud Mexico - Cloud, DevOps, AI & Machine Learning Services",
      description: description || "LogiCloud Mexico - Leading provider of cloud, DevOps, AI and machine learning services for startups and businesses. Specializing in cloud infrastructure, DevOps automation, artificial intelligence, machine learning, route planner, driving directions, map navigation, and innovative mobile applications in Mexico.",
      keywords: keywords || "cloud services Mexico, DevOps services Mexico, AI solutions Mexico, machine learning Mexico, cloud infrastructure, DevOps automation, artificial intelligence, machine learning, startup services, LogiCloud, Mexico tech services, AI development, cloud computing, ML services, route planner, free route planner, driving directions, map navigation, get directions, turn-by-turn directions, route planning, navigation app, GPS directions, map directions, worldwide route planner, international directions, global navigation",
      siteName: "LogiCloud Mexico"
    },
    es: {
      title: title || "LogiCloud México - Servicios de Cloud, DevOps, IA y Machine Learning",
      description: description || "LogiCloud México - Proveedor líder de servicios de cloud, DevOps, IA y machine learning para startups y empresas. Especializándonos en infraestructura en la nube, automatización DevOps, inteligencia artificial, machine learning, planificador de rutas, indicaciones de conducción, navegación por mapas y aplicaciones móviles innovadoras en México.",
      keywords: keywords || "servicios cloud México, servicios DevOps México, soluciones IA México, machine learning México, infraestructura cloud, automatización DevOps, inteligencia artificial, machine learning, servicios startups, LogiCloud, servicios tech México, desarrollo IA, computación cloud, servicios ML, planificador de rutas, planificador de rutas gratis, indicaciones de conducción, navegación por mapas, obtener direcciones, direcciones paso a paso, planificación de rutas, aplicación de navegación, direcciones GPS, direcciones de mapas, planificador de rutas mundial, direcciones internacionales, navegación global",
      siteName: "LogiCloud México"
    }
  }

  const currentContent = seoContent[language as keyof typeof seoContent] || seoContent.en
  
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://logicloud.mx/#organization",
        "name": currentContent.siteName,
        "alternateName": "logicloud.mx",
        "url": "https://logicloud.mx/",
        "logo": {
          "@type": "ImageObject",
          "url": "https://logicloud.mx/logo.png",
          "width": 200,
          "height": 200
        },
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Mexico City",
          "addressCountry": "MX"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+52-55-1234-5678",
          "contactType": "customer service",
          "email": "info@logicloud.mx"
        },
        "sameAs": [
          "https://www.linkedin.com/company/logicloud-limited",
          "https://noozter.com/",
          "https://noozter.com/track-map",
          "https://programmingalternatives.com/"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://logicloud.mx/#website",
        "url": "https://logicloud.mx/",
        "name": currentContent.siteName,
        "description": currentContent.description,
        "publisher": {
          "@id": "https://logicloud.mx/#organization"
        },
        "inLanguage": language === 'es' ? 'es-MX' : 'en-US',
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://logicloud.mx/?s={search_term_string}"
          },
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "WebPage",
        "@id": url,
        "url": url,
        "name": currentContent.title,
        "isPartOf": {
          "@id": "https://logicloud.mx/#website"
        },
        "description": currentContent.description,
        "inLanguage": language === 'es' ? 'es-MX' : 'en-US'
      }
    ]
  }

  return (
    <Helmet>
      {/* Language and Locale */}
      <html lang={language === 'es' ? 'es-MX' : 'en-US'} />
      <meta property="og:locale" content={language === 'es' ? 'es_MX' : 'en_US'} />
      
      {/* Primary Meta Tags */}
      <title>{currentContent.title}</title>
      <meta name="title" content={currentContent.title} />
      <meta name="description" content={currentContent.description} />
      <meta name="keywords" content={currentContent.keywords} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={currentContent.title} />
      <meta property="og:description" content={currentContent.description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={currentContent.siteName} />
      <meta property="og:locale" content={language === 'es' ? 'es_MX' : 'en_US'} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={currentContent.title} />
      <meta property="twitter:description" content={currentContent.description} />
      <meta property="twitter:image" content={image} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Language Alternatives */}
      <link rel="alternate" hrefLang="en" href="https://logicloud.mx/" />
      <link rel="alternate" hrefLang="es" href="https://logicloud.mx/" />
      <link rel="alternate" hrefLang="x-default" href="https://logicloud.mx/" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  )
}

export default SEO
